import { useState, useEffect, lazy, Suspense } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useLanguage } from '../../context/LanguageContext'
import { AppLayout } from '../../components/layouts/AppLayout'
const OnboardingTour = lazy(() => import('../../components/OnboardingTour'))
import { MetricCard } from '../../components/dashboard/MetricCard'
import { Button } from '../../components/ui/button'
import { FreightForwarderReviewsWidget } from '../../components/FreightForwarderReviewsWidget'
import {
  Package,
  CurrencyCircleDollar,
  TrendUp,
  ChartLine,
  Binoculars,
  Clock,
  Spinner,
  ArrowRight,
  Boat,
  Airplane,
  Truck,
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import { MultiCurrencyTotal } from '../../components/offer/MultiCurrencyTotal'
import { totalsFromLegacyOffer } from '../../utils/offerTotals'

interface FreightForwarderStats {
  activeOpportunities: number
  offersSent: number
  offersAccepted: number
  offersDeclined: number
  offersThisMonth: number
  activeOrders: number
  draftOffers: number
  conversionRate: number
}

interface RecentOffer {
  id: string
  status: string
  currency: string
  totalValue: number
  totalsByCurrency?: Record<string, number> | null
  createdAt: string
  updatedAt: string
  quoteTxControl: string
  shipperName: string
}

interface HotOpportunity {
  id: string
  txControl: string
  originCity: string
  originCountry: string
  destinationCity: string
  destinationCountry: string
  createdAt: string
  modalityName: string
  shipperName: string
}

interface ActiveOrder {
  id: string
  status: string
  quoteTxControl: string
  origin: { location?: { city?: string; country?: string } }
  destination: { location?: { city?: string; country?: string } }
  modalityName: string
  shipperName: string
  shipperTradingName?: string
  totalValue: number
  totalsByCurrency?: Record<string, number> | null
  currency: string
  createdAt: string
  estimatedDelivery?: string
}

export default function FreightForwarderDashboard() {
  const { user } = useAuth()
  const { t } = useLanguage()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<FreightForwarderStats | null>(null)
  const [recentOffers, setRecentOffers] = useState<RecentOffer[]>([])
  const [hotOpportunities, setHotOpportunities] = useState<HotOpportunity[]>([])
  const [activeOrders, setActiveOrders] = useState<ActiveOrder[]>([])
  const [error, setError] = useState<string | null>(null)
  const [showOnboarding, setShowOnboarding] = useState(false)

  // Show onboarding tour for new users
  useEffect(() => {
    if (user && user.onboarding_completed === false) {
      setShowOnboarding(true)
    }
  }, [user])

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true)
        setError(null)

        const [statsRes, activityRes, ordersRes] = await Promise.all([
          api.get('/freight-forwarder/stats'),
          api.get('/freight-forwarder/activity/recent'),
          api.get('/freight-forwarder/orders')
        ])

        setStats(statsRes.data.stats)
        setRecentOffers(activityRes.data.recentOffers || [])
        setHotOpportunities(activityRes.data.hotOpportunities || [])
        // Filter only active orders (not delivered or canceled)
        const orders = ordersRes.data.orders || []
        setActiveOrders(orders.filter((o: ActiveOrder) => !['delivered', 'canceled'].includes(o.status)).slice(0, 5))
      } catch (err: any) {
        console.error('Error fetching freight forwarder data:', err)
        setError(err.response?.data?.error || 'Erro ao carregar dados')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const formatCurrency = (value: number, currency: string = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency,
    }).format(value)
  }

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; color: string }> = {
      draft: { label: 'Rascunho', color: 'bg-gray-100 text-gray-700' },
      sent: { label: 'Enviada', color: 'bg-blue-100 text-blue-700' },
      accepted: { label: 'Aceita', color: 'bg-green-100 text-green-700' },
      declined: { label: 'Recusada', color: 'bg-red-100 text-red-700' },
      expired: { label: 'Expirada', color: 'bg-orange-100 text-orange-700' },
    }
    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-700' }
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    )
  }

  const getModalityIcon = (modalityName: string) => {
    const name = modalityName?.toLowerCase() || ''
    if (name.includes('mar')) return <Boat size={16} className="text-blue-600" />
    if (name.includes('aer') || name.includes('air')) return <Airplane size={16} className="text-purple-600" />
    if (name.includes('ter') || name.includes('rod')) return <Truck size={16} className="text-green-600" />
    return <Package size={16} className="text-gray-600" />
  }

  const timeAgo = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)

    if (diffHours < 1) return 'há poucos minutos'
    if (diffHours < 24) return `há ${diffHours}h`
    if (diffDays === 1) return 'há 1 dia'
    return `há ${diffDays} dias`
  }

  return (
    <AppLayout>
      {showOnboarding && (
        <Suspense fallback={null}>
          <OnboardingTour onComplete={() => setShowOnboarding(false)} />
        </Suspense>
      )}
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Dashboard Agente de Cargas
              </h1>
              <p className="text-sm text-gray-500">
                {t.dashboard.welcome}, {user?.name || (user as any)?.company?.name}
              </p>
            </div>
            <Button
              onClick={() => navigate('/freight-forwarder/opportunities')}
              size="lg"
              className="gap-2"
              style={{ backgroundColor: 'rgb(5, 150, 105)' }}
            >
              <Binoculars size={20} />
              Ver Oportunidades
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size={32} className="animate-spin text-emerald-600" />
            <span className="ml-3 text-gray-500">Carregando dados...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600">{error}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => window.location.reload()}
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {stats && (
          <>
            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <MetricCard
                title="Novas Oportunidades"
                value={stats.activeOpportunities}
                subtitle="Aguardando proposta"
                icon={Package}
                iconColor="rgb(59, 130, 246)"
              />
              <MetricCard
                title="Propostas Enviadas"
                value={stats.offersSent}
                subtitle={`${stats.draftOffers} em rascunho`}
                icon={Clock}
                iconColor="rgb(139, 92, 246)"
              />
              <MetricCard
                title="Negócios Ganhos"
                value={stats.offersAccepted}
                subtitle="Propostas aceitas"
                icon={TrendUp}
                iconColor="rgb(5, 150, 105)"
              />
              <MetricCard
                title="Taxa de Conversão"
                value={`${stats.conversionRate}%`}
                subtitle={`${stats.offersThisMonth} este mês`}
                icon={ChartLine}
                iconColor="rgb(251, 146, 60)"
              />
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Hot Opportunities */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <span className="animate-pulse text-red-500">🔥</span>
                        Oportunidades Quentes
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Cotações recentes que precisam de proposta
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  {hotOpportunities.length === 0 ? (
                    <div className="text-center py-8">
                      <Binoculars size={48} className="mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">Nenhuma oportunidade quente no momento</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {hotOpportunities.map((opportunity) => (
                        <div
                          key={opportunity.id}
                          className="flex items-start justify-between p-4 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all cursor-pointer"
                          onClick={() => navigate(`/freight-forwarder/opportunities/${opportunity.id}`)}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {getModalityIcon(opportunity.modalityName)}
                              <h3 className="font-semibold text-gray-900 text-sm">
                                {opportunity.txControl}
                              </h3>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              {opportunity.shipperName}
                            </p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                              <span>{opportunity.originCity}, {opportunity.originCountry}</span>
                              <ArrowRight size={12} />
                              <span>{opportunity.destinationCity}, {opportunity.destinationCountry}</span>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                              {timeAgo(opportunity.createdAt)}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="ml-4"
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/freight-forwarder/opportunities/${opportunity.id}`)
                            }}
                          >
                            Ver Detalhes
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 text-center">
                    <Button
                      variant="ghost"
                      onClick={() => navigate('/freight-forwarder/opportunities')}
                      className="w-full"
                    >
                      Ver Todas as Oportunidades
                    </Button>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Atividade Recente
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Suas últimas propostas e interações
                    </p>
                  </div>
                </div>
                <div className="p-6">
                  {recentOffers.length === 0 ? (
                    <div className="text-center py-8">
                      <CurrencyCircleDollar size={48} className="mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">Nenhuma proposta enviada ainda</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {recentOffers.map((offer) => (
                        <div
                          key={offer.id}
                          className="flex items-start gap-4 p-4 rounded-lg border border-gray-200 hover:border-gray-300 cursor-pointer transition-colors"
                          onClick={() => navigate(`/freight-forwarder/offers/${offer.id}`)}
                        >
                          <div
                            className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${
                              offer.status === 'accepted'
                                ? 'bg-green-500'
                                : offer.status === 'sent'
                                ? 'bg-blue-500'
                                : offer.status === 'declined'
                                ? 'bg-red-500'
                                : 'bg-gray-400'
                            }`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold text-gray-900">
                                {offer.quoteTxControl}
                              </p>
                              {getStatusBadge(offer.status)}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              {offer.shipperName}
                            </p>
                            <div className="flex items-center justify-between mt-1">
                              <p className="text-xs text-gray-500">
                                {timeAgo(offer.updatedAt)}
                              </p>
                              {offer.totalValue && (
                                <MultiCurrencyTotal
                                  totalsByCurrency={totalsFromLegacyOffer(offer)}
                                  variant="compact"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 text-center">
                    <Button
                      variant="ghost"
                      onClick={() => navigate('/freight-forwarder/offers')}
                      className="w-full"
                    >
                      Ver Todas as Propostas
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Active Shipments */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-8">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Package size={20} className="text-emerald-600" />
                      Embarques Ativos
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Propostas aceitas em andamento
                    </p>
                  </div>
                  {activeOrders.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/freight-forwarder/shipments')}
                    >
                      Ver Todos
                    </Button>
                  )}
                </div>
              </div>
              <div className="p-6">
                {activeOrders.length === 0 ? (
                  <div className="text-center py-8">
                    <Package size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500 mb-2">Nenhum embarque ativo no momento</p>
                    <p className="text-sm text-gray-400">
                      Quando uma proposta for aceita, o embarque aparecerá aqui
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {activeOrders.map((order) => (
                      <div
                        key={order.id}
                        className="p-4 rounded-lg border border-gray-200 hover:border-emerald-300 hover:shadow-sm transition-all cursor-pointer"
                        onClick={() => navigate(`/freight-forwarder/orders/${order.id}`)}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {getModalityIcon(order.modalityName)}
                            <span className="font-semibold text-gray-900 text-sm">
                              {order.quoteTxControl}
                            </span>
                          </div>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            order.status === 'in_transit' ? 'bg-blue-100 text-blue-700' :
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            order.status === 'processing' ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {order.status === 'in_transit' ? 'Em Trânsito' :
                             order.status === 'pending' ? 'Pendente' :
                             order.status === 'processing' ? 'Processando' :
                             order.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{order.shipperTradingName || order.shipperName}</p>
                        <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                          <span>{order.origin?.location?.city || 'Origem'}</span>
                          <ArrowRight size={10} />
                          <span>{order.destination?.location?.city || 'Destino'}</span>
                        </div>
                        <div className="flex items-start justify-between">
                          <span className="text-xs text-gray-400">{timeAgo(order.createdAt)}</span>
                          <MultiCurrencyTotal
                            totalsByCurrency={totalsFromLegacyOffer(order)}
                            variant="compact"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-700 font-medium">
                      Taxa de Conversão
                    </p>
                    <p className="text-3xl font-bold text-blue-900 mt-2">{stats.conversionRate}%</p>
                    <p className="text-xs text-blue-600 mt-1">
                      {stats.offersAccepted} de {stats.offersAccepted + stats.offersDeclined} propostas
                    </p>
                  </div>
                  <TrendUp size={40} className="text-blue-600 opacity-50" />
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-700 font-medium">
                      Pedidos Ativos
                    </p>
                    <p className="text-3xl font-bold text-purple-900 mt-2">{stats.activeOrders}</p>
                    <p className="text-xs text-purple-600 mt-1">
                      Em andamento
                    </p>
                  </div>
                  <Clock size={40} className="text-purple-600 opacity-50" />
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-700 font-medium">
                      Propostas Este Mês
                    </p>
                    <p className="text-3xl font-bold text-green-900 mt-2">{stats.offersThisMonth}</p>
                    <p className="text-xs text-green-600 mt-1">
                      {stats.offersSent} aguardando resposta
                    </p>
                  </div>
                  <TrendUp size={40} className="text-green-600 opacity-50" />
                </div>
              </div>
            </div>

            <FreightForwarderReviewsWidget />
          </>
        )}
      </main>
    </AppLayout>
  )
}
